import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../../contexts/CurrentUser';
import { ReactComponent as NarrativeIcon } from '../../../pages/NarrativeAddPage/assets/narrative.svg';
import { ReactComponent as ManualIcon } from '../../../pages/NarrativeAddPage/assets/manual.svg';
import { ReactComponent as ImpactIcon } from '../../../pages/NarrativeAddPage/assets/impact.svg';
import { ReactComponent as AiIcon } from '../../../assets/ai.svg';
import styles from './NarrativeTypeChooser.module.scss';

type NarrativeTypeChooserProps = {
  currentNarrativeTypeIndex: number | 0;
  onChange: (selected: number) => void;
  error?: string;
  isDisabled?: boolean;
  isEditMode?: boolean;
};

type NarrativeType = {
  type: string;
  title: string;
  icon: React.ReactNode;
  description: string;
};

export const NarrativeTypeChooser: React.FC<NarrativeTypeChooserProps> = ({
  currentNarrativeTypeIndex,
  onChange,
  error,
  isDisabled,
  isEditMode = false,
}) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const [isCaseTypeChangeDisabled, setIsCaseTypeChangeDisabled] =
    useState(false);

  type WorkspaceConfig = {
    featureflags: string[];
    available_case_types?: string[];
  };

  const workspaceConfig: WorkspaceConfig | undefined =
    currentUser?.workspace?.config;
  const availableCaseTypes = workspaceConfig?.available_case_types || [];

  const narrativeTypes = [
    {
      type: 'NARRATIVE',
      title: t('Narrative'),
      icon: <NarrativeIcon />,
      description: t(
        'Case auto-collects topic data from queries, supports AI/boolean and stories.',
      ),
    },
    {
      type: 'MANUAL',
      title: t('Manual'),
      icon: <ManualIcon />,
      description: t(
        'Empty case to which you can manually add messages from search and other cases.',
      ),
    },
    {
      type: 'TOPIC_MODELING',
      title: t('Topic modeling'),
      icon: <ImpactIcon />,
      description: t(
        'Case uses AI to group publications by topics and detect narratives across regions and languages.',
      ),
    },
  ];

  const currentType = narrativeTypes[currentNarrativeTypeIndex]?.type;

  const isTMDisabled =
    currentType !== 'TOPIC_MODELING' && isEditMode;

  useEffect(() => {
    if (currentType === 'TOPIC_MODELING' && isEditMode) {
      setIsCaseTypeChangeDisabled(true);
    }
  }, []);

  return (
    <div className={error ? 'form-element error' : 'form-element'}>
      <label>{t('Case types')}</label>
      <div className={styles.narrativeTypeContainer}>
        {narrativeTypes.map((narrativeType: NarrativeType, index) => {
          if (!availableCaseTypes.includes(narrativeType.type)) {
            return null;
          }
          return (
            <div
              key={narrativeType.type}
              className={[
                styles.narrativeTypeCard,
                narrativeTypes[currentNarrativeTypeIndex].type ===
                narrativeType.type
                  ? styles.selected
                  : '',
                isDisabled ||
                (isCaseTypeChangeDisabled &&
                  narrativeType.type !== 'TOPIC_MODELING') ||
                (isTMDisabled && narrativeType.type === 'TOPIC_MODELING')
                  ? styles.disabled
                  : '',
              ].join(' ')}
              onClick={() =>
                !isDisabled &&
                !isCaseTypeChangeDisabled &&
                !(isTMDisabled && narrativeType.type === 'TOPIC_MODELING') &&
                onChange(index)
              }
            >
              <div className={styles.narrativeTypeIconContainer}>
                <div className={styles.narrativeTypeIcon}>
                  {narrativeType.icon}
                  {narrativeType.type === 'TOPIC_MODELING' ? (
                    <div className={styles.aiIcon}>
                      <AiIcon />
                    </div>
                  ) : null}
                </div>
              </div>
              <div className={styles.narrativeTypeContent}>
                <div className={styles.narrativeTypeTitle}>
                  {narrativeType.title}
                </div>
                <p className={styles.narrativeTypeDescription}>
                  {narrativeType.description}
                </p>
              </div>
            </div>
          );
        })}
        {availableCaseTypes.length === 0 && (
          <p>
            {t(
              'No narrative types are available. Please contact your administrator.',
            )}
          </p>
        )}
      </div>
      {error && <div className="errorMessage">{error}</div>}
    </div>
  );
};
